import {
  Component,
  lazy,
  Suspense,
  type ComponentType,
  type ErrorInfo,
  type LazyExoticComponent,
  type ReactNode,
} from 'react';
import { CenteredSpinner } from '../components/Spinner.tsx';

/**
 * Route-level code splitting, made safe across deploys.
 *
 * Each SPA ships as hashed chunks, and a deploy REPLACES them: a tab opened
 * before the deploy still runs the old entry chunk, whose dynamic imports name
 * chunk files that no longer exist. The first navigation to a not-yet-loaded
 * page then fails to import — and React, given a rejected lazy(), unmounts the
 * whole tree: a white screen. So a failed page import is retried once (a
 * transient network blip), and if it fails again the page reloads, which
 * fetches the new index.html and with it the new chunk names.
 *
 * The reload is guarded by a sessionStorage flag so a chunk that is genuinely
 * unreachable (offline, a broken deploy) cannot reload forever: the second
 * failure in a row surfaces through LazyBoundary's message instead. Any
 * successful page import clears the flag.
 */

export const CHUNK_RELOAD_FLAG = 'l2u-chunk-reload';

export type ChunkReloadDeps = {
  session: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
  reload: () => void;
};

function browserDeps(): ChunkReloadDeps {
  let session: ChunkReloadDeps['session'] = null;
  try {
    session = typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    session = null;
  }
  return { session, reload: () => window.location.reload() };
}

/**
 * The single-reload guard shared by importWithReload and the vite:preloadError
 * handler: reloads the page and returns true only if no chunk reload has run
 * in this tab yet AND the flag recording it could be written. Otherwise
 * returns false and does nothing — the caller lets the failure surface (the
 * LazyBoundary message) instead of risking a reload loop.
 */
export function reloadOnceForChunks(deps: ChunkReloadDeps): boolean {
  if (!deps.session) return false;
  try {
    if (deps.session.getItem(CHUNK_RELOAD_FLAG) !== null) return false;
    deps.session.setItem(CHUNK_RELOAD_FLAG, String(Date.now()));
  } catch {
    return false;
  }
  deps.reload();
  return true;
}

/**
 * Vite's own signal for the same stale-deploy failure: before a lazy chunk is
 * imported, its CSS and dependency chunks are PRELOADED, and when one of those
 * 404s Vite dispatches `vite:preloadError` on window (and throws unless the
 * event is cancelled). Per Vite's docs the fix is a reload — done here through
 * the same once-per-tab guard. When the guard refuses, the event is left
 * alone so the error reaches importWithReload and LazyBoundary as before.
 */
export function handlePreloadError(
  event: { preventDefault(): void },
  deps: ChunkReloadDeps = browserDeps(),
): boolean {
  if (!reloadOnceForChunks(deps)) return false;
  event.preventDefault();
  return true;
}

let preloadHandlerInstalled = false;

// Installed on the first lazyPage() call — i.e. while App.tsx is evaluated at
// boot — rather than as a module side effect, which this package's
// "sideEffects": false would let the bundler drop.
function installPreloadErrorHandler(): void {
  if (preloadHandlerInstalled || typeof window === 'undefined') return;
  preloadHandlerInstalled = true;
  window.addEventListener('vite:preloadError', (event) => {
    handlePreloadError(event);
  });
}

/**
 * Run a dynamic import; on failure retry once, then reload the page once.
 *
 * Resolves with the module, or — when it has started a reload — never settles,
 * so nothing renders a half-state while the page goes away. Rejects (for the
 * boundary to show) when the import still fails after a reload already ran in
 * this tab, or when there is no sessionStorage to guard a reload with.
 */
export async function importWithReload<T>(
  load: () => Promise<T>,
  deps: ChunkReloadDeps = browserDeps(),
): Promise<T> {
  let result: T;
  try {
    result = await load();
  } catch {
    try {
      result = await load();
    } catch (err) {
      if (!reloadOnceForChunks(deps)) throw err;
      return new Promise<T>(() => {});
    }
  }
  try {
    deps.session?.removeItem(CHUNK_RELOAD_FLAG);
  } catch {
    // Nothing to re-arm on storage we cannot touch.
  }
  return result;
}

/**
 * React.lazy for a NAMED page export, through importWithReload:
 *   const Drivers = lazyPage(() => import('./Drivers.tsx').then((m) => m.Drivers));
 */
export function lazyPage<P extends object>(
  load: () => Promise<ComponentType<P>>,
): LazyExoticComponent<ComponentType<P>> {
  installPreloadErrorHandler();
  return lazy(() => importWithReload(load).then((component) => ({ default: component })));
}

/**
 * True for the error a failed dynamic import throws — the chunk file could not
 * be fetched (offline, or a deploy removed it). The wording differs by
 * browser: Chromium "Failed to fetch dynamically imported module", Firefox
 * "error loading dynamically imported module", Safari "Importing a module
 * script failed", webpack-style loaders "Loading chunk N failed", and Vite's
 * own preload helper "Unable to preload CSS for /assets/…".
 */
const CHUNK_ERROR_RE =
  /dynamically imported module|Importing a module script failed|Loading (CSS )?chunk|Unable to preload/i;

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return CHUNK_ERROR_RE.test(error.message);
}

/** What LazyBoundary says in place of a page that failed. */
export function lazyBoundaryMessage(error: unknown): string {
  return isChunkLoadError(error)
    ? 'This page could not be loaded. Check your connection and try again.'
    : 'Something went wrong showing this page. Reload to try again.';
}

type BoundaryState = { error: unknown; failed: boolean };

/**
 * Suspense + an error boundary for lazy pages: a spinner while the chunk loads,
 * and — if it cannot load even after the reload, or the page throws while
 * rendering — a message with a Reload button in place of the page, never an
 * unmounted (white) app. Placed around each page element, inside its shell, so
 * the shell's navigation stays usable. Only a chunk-load failure is blamed on
 * the connection; any other error gets a generic message and is logged.
 */
export class LazyBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  override state: BoundaryState = { error: null, failed: false };

  static getDerivedStateFromError(error: unknown): BoundaryState {
    return { error, failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // No error-reporting service in this app: the console is where a page
    // crash is visible (devtools, and any log capture on the device).
    console.error('Page failed to render', error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.failed) {
      return (
        <div className="flex h-64 flex-col items-center justify-center gap-3 px-4 text-center text-sm text-slate-600">
          <p>{lazyBoundaryMessage(this.state.error)}</p>
          <button
            type="button"
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      );
    }
    return <Suspense fallback={<CenteredSpinner />}>{this.props.children}</Suspense>;
  }
}
